"use client";

import React, { Suspense, useCallback, useRef, useState } from "react";
import { StyledDiv } from "./widgetCatalog.styles";
import { IoMdClose } from "@react-icons/all-files/io/IoMdClose";
import { AiFillStar } from "@react-icons/all-files/ai/AiFillStar";
import { AiFillFire } from "@react-icons/all-files/ai/AiFillFire";
import { RiLeafFill } from "@react-icons/all-files/ri/RiLeafFill";
import Image from "next/image";
import Link from "next/link";

import { IPlugin, INewCategory, INewTag, IPage, IPlatform } from "../../types";
import { Button } from "../button/button.comp";
import { useRouter, useSearchParams } from "next/navigation";
import { debounce } from "lodash";

interface IWidgetCatalogProps {
  apps: IPlugin[];
  pages?: IPage[];
  platform?: IPlatform;
  hideHome?: boolean;
  categories: INewCategory[];
  tags: INewTag[];
}

export const WidgetCatalog = ({
  apps,
  platform,
  pages,
  hideHome = false,
  categories,
  tags,
}: IWidgetCatalogProps) => {
  return (
    <StyledDiv>
      <Suspense>
        <Catalog
          apps={apps}
          platform={platform}
          pages={pages}
          hideHome={hideHome}
          categories={categories}
          tags={tags}
        />
      </Suspense>
    </StyledDiv>
  );
};

function Catalog({
  apps,
  platform,
  pages,
  hideHome = false,
  categories,
  tags,
}: IWidgetCatalogProps) {
  const searchParams = useSearchParams();
  const searchQuery = searchParams?.get("search") || "";
  const router = useRouter();
  const listRef = useRef<any>();
  const [filter, setFilter] = useState({
    page: "",
    name: searchQuery || "",
    tag: "",
    cat: "",
  });
  const pluginList = apps.sort((a, b) => a.order - b.order);
  const searchText = filter.name?.toLowerCase() || "";

  const tagList = pluginList.reduce((acc: string[], plugin: IPlugin) => {
    (plugin.newTags || []).forEach((tag) => {
      const wantedTag = tags.find((t) => t._id === tag._id);
      if (wantedTag && !acc.includes(wantedTag.name)) {
        acc.push(wantedTag.name);
      }
    });
    return acc;
  }, []);

  const catList = pluginList.reduce((acc: any, plugin: IPlugin) => {
    (plugin.newCategories || []).forEach((category) => {
      const wantedCat = categories.find((c) => c._id === category._id);

      if (wantedCat && !acc[wantedCat.name]) acc[wantedCat.name] = 1;
      else acc[wantedCat?.name as string] = acc[wantedCat?.name as string] + 1;
    });
    return acc;
  }, {});

  const weightedSearch = () => {
    return pluginList
      .map((plugin) => {
        let score = 0;

        // Assign scores based on match
        if (plugin.name?.toLowerCase().includes(searchText)) {
          score += 3; // Higher weight for name matches
        }
        if (plugin.teaser?.toLowerCase().includes(searchText)) {
          score += 2; // Medium weight for teaser matches
        }
        if (plugin.description?.toLowerCase().includes(searchText)) {
          score += 1; // Lower weight for description matches
        }

        return { plugin, score };
      })
      .filter(({ score }) => score > 0) // Filter out plugins with no match
      .sort((a, b) => b.score - a.score) // Sort by score in descending order
      .map(({ plugin }) => plugin); // Return the sorted plugins
  };

  const filteredPlugins = weightedSearch()
    .filter((p) =>
      filter.tag ? (p.newTags || []).map((t) => t.name).includes(filter.tag) : p
    )
    .filter((p) =>
      filter.cat
        ? (p.newCategories || []).map((t) => t.name).includes(filter.cat)
        : p
    );

  const debouncedSearch = useCallback(
    debounce((val: string) => {
      const baseUrl = platform?.slug
        ? `/platforms/${platform.slug}`
        : "/widgets";
      const url = `${baseUrl}?search=${encodeURIComponent(val)}`;
      router.replace(url);
    }, 300), // 300ms delay
    [router]
  );

  function handleSearchChange(value: string) {
    setFilter((prevFilter) => ({
      ...prevFilter,
      name: value,
    }));
    debouncedSearch(value);
  }

  function renderSidebar() {
    return (
      <div className="filter-container">
        <form
          className="search-components"
          onSubmit={(e) => e.preventDefault()}
        >
          <input
            name="searchType"
            title="Search Widgets"
            placeholder="Search widgets..."
            value={filter.name}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {filter.name && (
            <IoMdClose
              onClick={() => handleSearchChange("")}
              className="reset"
              fontSize={20}
            />
          )}
        </form>
        <div className="categories">
          {!hideHome && (
            <>
              <button
                className={`cat ${filter.page === "home" ? "active" : ""}`}
                onClick={() =>
                  setFilter({
                    ...filter,
                    cat: "",
                    tag: "",
                    name: "",
                    page: "home",
                  })
                }
              >
                Home
              </button>
              <div className="divider"></div>
            </>
          )}
          <p className="cat-title">Categories</p>
          <button
            className={`cat ${
              !filter.page && !filter.tag && !filter.cat ? "active" : ""
            }`}
            onClick={() =>
              setFilter({ ...filter, page: "", cat: "", tag: "", name: "" })
            }
          >
            All ({pluginList.length})
          </button>
          {Object.keys(catList).map((cat) => (
            <button
              key={cat}
              className={`cat ${
                !filter.page && filter.cat === cat ? "active" : ""
              }`}
              onClick={() => {
                setFilter({ ...filter, page: "", tag: "", name: "", cat });
                if (listRef.current && filter.cat) {
                  listRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
              }}
            >
              {cat.split("-")[0]} ({catList[cat]})
            </button>
          ))}
        </div>
      </div>
    );
  }

  function renderApps(appsList: IPlugin[]) {
    if (pluginList.length === 0) {
      return <div className="apps-container">Loading...</div>;
    }

    return (
      <div ref={listRef} className={`apps-container`}>
        {appsList.length > 0 &&
          appsList.map((plugin: IPlugin) => {
            let path = `/widgets/${plugin.slug}`;

            if (platform) {
              const pageExists = (pages || []).some(
                (page) => page.appId === plugin._id
              );

              path = pageExists
                ? `/widgets/${plugin.slug}/${platform?.slug}`
                : `/widgets/${plugin.slug}`;
            }

            return (
              <Link
                title={`${plugin.name} ${
                  platform?.title ? `for ${platform?.title}` : ""
                }`}
                key={plugin.name}
                className="app"
                href={path}
              >
                <Image
                  src={plugin.catalogImage}
                  alt={`${plugin.name} ${
                    platform?.title ? `for ${platform?.title}` : ""
                  } logo`}
                  width={320}
                  height={170}
                />
                <div className="details">
                  <h3>
                    {plugin.name}{" "}
                    {platform?.title ? `for ${platform?.title}` : ""}
                  </h3>
                  <p>{plugin.description || plugin.teaser}</p>
                </div>
              </Link>
            );
          })}
        {!appsList.length && (
          <div className="no-items">
            <Image
              src={"/assets/logo-mask.png"}
              width={100}
              height={50}
              alt="common ninja logo"
            />
            <p>Widget not found.</p>
            <Button
              onClick={(e) => {
                e.preventDefault();
                window.open("https://commoninja.site/suggest-a-widget");
              }}
              style={{ marginLeft: 20, fontSize: 14 }}
            >
              Suggest a Widget+
            </Button>
          </div>
        )}
      </div>
    );
  }

  function renderHome() {
    return (
      <div className="widget-catalog-home">
        {tagList.map((tag) => {
          return (
            <div className="home-section" key={tag}>
              <h3>
                {tag.toLowerCase().includes("popular") && (
                  <AiFillStar className="icon" />
                )}
                {tag.toLowerCase().includes("trending") && (
                  <AiFillFire className="icon" />
                )}
                {tag.toLowerCase().includes("new") && (
                  <RiLeafFill className="icon" />
                )}
                {tag}
              </h3>
              {renderApps(
                pluginList.filter((p) =>
                  (p.newTags || []).map((t) => t.name).includes(tag)
                )
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="inner">
      <div className="main-content-wrapper">
        {renderSidebar()}
        {filter.page === "home" ? renderHome() : renderApps(filteredPlugins)}
      </div>
    </div>
  );
}
